'use client';

import dynamic from 'next/dynamic';
import { useEffect, useState } from 'react';
import type { GlobeConfig } from '@/components/ui/globe';

const World = dynamic(() => import('@/components/ui/globe').then((m) => m.World), {
  ssr: false,
  loading: () => <div className="hero__globe-skeleton" aria-hidden="true" />
});

// Lighter frost palette (not navy-black).
const globeConfig: GlobeConfig = {
  pointSize: 3,
  globeColor: '#00558d',
  showAtmosphere: true,
  atmosphereColor: '#bcdcf8',
  atmosphereAltitude: 0.12,
  emissive: '#003f6b',
  emissiveIntensity: 0.14,
  shininess: 0.9,
  polygonColor: 'rgba(222, 238, 255, 0.85)',
  ambientLight: '#8ec5f0',
  directionalLeftLight: '#ffffff',
  directionalTopLight: '#ffffff',
  pointLight: '#cfe6fb',
  arcTime: 1400,
  arcLength: 0.9,
  rings: 1,
  maxRings: 3,
  initialPosition: { lat: 26.7153, lng: -80.0534 },
  autoRotate: true,
  autoRotateSpeed: 0.5
};

// Frost accent palette, derived from the FrostCanvas orb hues (HSL -> hex).
const ACCENTS = ['#7ba7f4', '#556ee7', '#9ad6f4', '#8178e2'];

// Major hubs worldwide. West Palm Beach is the anchor; arcs span the globe.
const C: Record<string, [number, number]> = {
  wpb: [26.7153, -80.0534],
  nyc: [40.7128, -74.006],
  la: [34.0522, -118.2437],
  sf: [37.7749, -122.4194],
  chi: [41.8781, -87.6298],
  tor: [43.6532, -79.3832],
  mex: [19.4326, -99.1332],
  sao: [-23.5505, -46.6333],
  bue: [-34.6037, -58.3816],
  lon: [51.5072, -0.1276],
  par: [48.8566, 2.3522],
  ber: [52.52, 13.405],
  mad: [40.4168, -3.7038],
  lag: [6.5244, 3.3792],
  joh: [-26.2041, 28.0473],
  cai: [30.0444, 31.2357],
  dub: [25.2048, 55.2708],
  mum: [19.076, 72.8777],
  sin: [1.3521, 103.8198],
  hk: [22.3193, 114.1694],
  tok: [35.6762, 139.6503],
  seo: [37.5665, 126.978],
  syd: [-33.8688, 151.2093],
  mos: [55.7558, 37.6173]
};

const ROUTES: [string, string][] = [
  ['wpb', 'nyc'],
  ['wpb', 'la'],
  ['wpb', 'lon'],
  ['wpb', 'sao'],
  ['wpb', 'tok'],
  ['wpb', 'syd'],
  ['wpb', 'dub'],
  ['wpb', 'mex'],
  ['wpb', 'lag'],
  ['wpb', 'sin'],
  ['nyc', 'lon'],
  ['nyc', 'la'],
  ['lon', 'par'],
  ['lon', 'dub'],
  ['par', 'ber'],
  ['ber', 'mos'],
  ['la', 'tok'],
  ['sf', 'sin'],
  ['tok', 'seo'],
  ['hk', 'sin'],
  ['sin', 'syd'],
  ['mum', 'dub'],
  ['sao', 'bue'],
  ['sao', 'lag'],
  ['lag', 'joh'],
  ['cai', 'dub'],
  ['mex', 'mad'],
  ['tor', 'lon'],
  ['hk', 'la'],
  ['joh', 'lon'],
  ['mad', 'nyc'],
  ['seo', 'sf']
];

const arcs = ROUTES.map(([a, b], i) => ({
  order: (i % 8) + 1,
  startLat: C[a][0],
  startLng: C[a][1],
  endLat: C[b][0],
  endLng: C[b][1],
  arcAlt: 0.1 + (i % 5) * 0.045,
  color: ACCENTS[i % ACCENTS.length]
}));

export default function WorldGlobe() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // The globe is a heavy WebGL (three.js) decoration — ~760 KiB that costs
    // ~1.5s of main-thread time to evaluate and build. Loading it during the
    // initial page load wrecks mobile Total Blocking Time / Time to Interactive
    // (it added ~18 Lighthouse points when loaded eagerly). So gate it on the two
    // moments where the cost is free: genuine browser idle (fires ~1-2s after a
    // real device settles), or the visitor's first interaction. Either way it
    // never competes with first paint, font loading, or hydration. Mobile/touch
    // also gets a lighter variant — see `World`/`Globe` in components/ui/globe.tsx.
    let fired = false;
    let idleId: number | undefined;
    let timerId: ReturnType<typeof setTimeout> | undefined;
    const engage = ['pointerdown', 'pointermove', 'touchstart', 'keydown', 'wheel', 'scroll'];

    const cleanup = () => {
      engage.forEach((e) => window.removeEventListener(e, trigger));
      if (idleId !== undefined) window.cancelIdleCallback?.(idleId);
      if (timerId !== undefined) clearTimeout(timerId);
    };

    const trigger = () => {
      if (fired) return;
      fired = true;
      cleanup();
      setShow(true);
    };

    // Primary path: load the instant the visitor engages. On a real device the
    // first scroll/tap/mouse-move happens within a second or two, so the globe
    // appears promptly for anyone actually looking at the page.
    engage.forEach((e) => window.addEventListener(e, trigger, { once: true, passive: true }));

    // Backstop for a visitor who never interacts: load at the next idle, but only
    // after the page has had time to fully settle, so the heavy three.js evaluation
    // lands well after first paint / interactivity rather than competing with it.
    timerId = setTimeout(() => {
      if (typeof window.requestIdleCallback === 'function') {
        idleId = window.requestIdleCallback(trigger, { timeout: 2000 });
      } else {
        trigger();
      }
    }, 5000);

    return cleanup;
  }, []);

  if (!show) return <div className="hero__globe-skeleton" aria-hidden="true" />;
  return <World data={arcs} globeConfig={globeConfig} />;
}
